import React, { useEffect, useState, useRef } from 'react';
import utils from "../services/utils";
import managetables from '../services/managetables';
import CanvasTableMap from '../comps/CanvasTableMap';
import manageareas from '../services/manageareas';
import managecookies from "../services/managecookies";
import '../style/css/tablepage.css';
import Loading from "../comps/Loading";
import Icon from '../comps/Icon';

const TablePage = ({ areaLocked }) => {
    const [tables, setTables] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState('host');
    const [mode, setMode] = useState('LIVE'); // 'LIVE' or 'EDIT'
    const [selectedTable, setSelectedTable] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
    const [showAddMenu, setShowAddMenu] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [pendingShape, setPendingShape] = useState(null);
    const [newTableName, setNewTableName] = useState('');
    const [newTableDiners, setNewTableDiners] = useState('4');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [tableToDelete, setTableToDelete] = useState(null);

    useEffect(() => {
        const checkUser = () => {
            const userData = JSON.parse(managecookies.getCookieVal('userData') || '{}');
            const userRole = userData.user?.role || 'host';
            setRole(userRole);
        }
        checkUser();
        fetchTables();
        fetchAreas();

        // Refresh interval for live view
        const interval = setInterval(fetchTables, 10000);
        return () => clearInterval(interval);
    }, []);

    // Filter tables based on selected area
    const displayedTables = tables.filter(t => {
        const selectedId = parseInt(areaLocked);
        if (!selectedId || selectedId === 0) return true; // Show all
        // area_id comes from PHP as a string ("51"); coerce so it matches the numeric id.
        return Number(t.area_id) === selectedId;
    });

    const fetchAreas = async () => {
        const areaList = await manageareas.getAllArea(true);
        if (areaList && areaList.length > 0) setAreas(areaList);
    }

    const fetchTables = async () => {
        // In edit mode, we might want to pause auto-refresh or handle it carefully
        // For now, let's just fetch.
        const data = await managetables.getAllTables(true);
        if (data) setTables(data);
        setLoading(false);
    };

    const handleTableUpdate = async (updatedTable) => {
        // Optimistic update
        setTables(prev => prev.map(t => t.id === updatedTable.id ? updatedTable : t));
        await managetables.updateTable(updatedTable);
    };

    const [editingTableId, setEditingTableId] = useState(null);

    const initiateAddTable = (shapeType) => {
        setEditingTableId(null);
        setPendingShape(shapeType);
        setNewTableName('');
        setNewTableDiners('4');
        setShowModal(true);
    };

    const initiateEditTable = (table) => {
        setEditingTableId(table.id);
        setNewTableName(table.name);
        setNewTableDiners(table.diners);
        setPendingShape(table.shape);
        setShowModal(true);
    };

    const confirmAddOrUpdateTable = async () => {
        if (!newTableName) return;

        if (editingTableId) {
            // Update existing
            const tableToUpdate = tables.find(t => t.id === editingTableId);
            if (!tableToUpdate) return;

            const updatedTable = {
                ...tableToUpdate,
                name: newTableName,
                diners: parseInt(newTableDiners) || tableToUpdate.diners,
                // Shape usually doesn't change but if we want to allow it we can if we had a selector.
                // For now, keep original shape or whatever logic intended.
                // If the user wants to change shape they might delete and re-add, but let's assume just Name/Diners for now.
            };

            await handleTableUpdate(updatedTable);
            setShowModal(false);
            setEditingTableId(null);
        } else {
            // Add New
            // Use selected area, or default to first available, or fallback to 3 (assuming 1/2 are reserved/seq start)
            let targetAreaId = parseInt(areaLocked);
            if (!targetAreaId || targetAreaId === 0) {
                targetAreaId = areas.length > 0 ? areas[0].id : 3;
            }

            const newTable = {
                name: newTableName,
                x: 100 + (displayedTables.length * 20), // Stagger positions based on visible
                y: 100 + (displayedTables.length * 20),
                diners: parseInt(newTableDiners) || 4,
                shape: pendingShape, // 'circle', 'rectangle', 'triangle'
                is_locked: 0,
                area_id: targetAreaId
            };
            const added = await managetables.addTable(newTable);
            if (added) {
                fetchTables();
                setShowModal(false);
                setPendingShape(null);
            }
        }
    };

    const handleDeleteTable = async (id) => {
        setTableToDelete(id);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteTable = async () => {
        if (tableToDelete) {
            await managetables.deleteTable(tableToDelete);
            setTables(prev => prev.filter(t => t.id !== tableToDelete));
            setSelectedTable(null);
        }
        setShowDeleteConfirm(false);
        setTableToDelete(null);
    };

    const handleLockToggle = async (table) => {
        const newStatus = table.is_locked ? 0 : 1;
        await handleTableUpdate({ ...table, is_locked: newStatus });
        setSelectedTable(prev => prev && prev.id === table.id ? { ...prev, is_locked: newStatus } : prev);
    };
    const canEdit = ['owner', 'manager'].includes(role);
    // Check if current area allows editing (must be a sub-area)
    const areaIdNum = parseInt(areaLocked);
    // Area IDs come from PHP as strings ("51"); coerce so the lookup matches the numeric id
    // (otherwise selectedArea is undefined → isSubArea false → the sketch stays view-only).
    const selectedArea = areas.find(a => Number(a.id ?? a.ID) === areaIdNum);
    // A sub-area has a non-zero/non-null subToID
    const isSubArea = selectedArea && selectedArea.subToID && selectedArea.subToID !== 0 && selectedArea.subToID !== "0";
    const canEnterEditMode = canEdit && isSubArea;

    // Auto-switch to LIVE if we lose edit permission (e.g. switched to All or Main Area)
    useEffect(() => {
        if (mode === 'EDIT' && !canEnterEditMode) {
            setMode('LIVE');
        }
    }, [areaLocked, canEnterEditMode, mode]);

    if (loading) return <Loading />;

    return (
        <div
            className="table-page-container"
            onClick={() => setSelectedTable(null)}
            onContextMenu={(e) => e.preventDefault()}
        >
            {/* Top Toolbar */}
            <div className="toolbar" onClick={e => e.stopPropagation()}>
                <div className="left-tools">
                    <h2 className="page-title">
                        {mode === 'LIVE' ? (utils.useLang('LIVE_VIEW') || 'תצוגה חיה') : (utils.useLang('MANAGEMENT_MODE') || 'עריכת מפה')}
                        {!isSubArea && canEdit && <span className="page-title-hint"> (בחרו תת-אזור לעריכה)</span>}
                    </h2>
                </div>

                <div className="right-tools">
                    {canEdit && (
                        <button
                            className={`mode-btn ${mode === 'EDIT' ? 'active' : ''}`}
                            onClick={() => setMode(mode === 'LIVE' ? 'EDIT' : 'LIVE')}
                            disabled={!isSubArea}
                            style={{ opacity: isSubArea ? 1 : 0.5, cursor: isSubArea ? 'pointer' : 'not-allowed' }}
                            title={isSubArea ? "מעבר למצב עריכה" : "בחרו תת-אזור כדי לערוך את המפה"}
                        >
                            {mode === 'LIVE' ? (utils.useLang('SWITCH_TO_AB') || 'עריכת מפה') : (utils.useLang('SWITCH_TO_REAL') || 'תצוגה חיה')}
                        </button>
                    )}
                </div>
            </div>

            {/* Edit Mode Controls */}
            {mode === 'EDIT' && (
                <div className="edit-controls" onClick={e => e.stopPropagation()}>
                    <span className="label">{utils.useLang('ADD_TABLE_LABEL') || 'הוספת שולחן:'}</span>
                    <button className="shape-btn circle" onClick={() => initiateAddTable('circle')}>⬤ {utils.useLang('CIRCLE') || 'עיגול'}</button>
                    <button className="shape-btn square" onClick={() => initiateAddTable('square')}>◼ {utils.useLang('SQUARE') || 'ריבוע'}</button>
                    <button className="shape-btn rectangle" onClick={() => initiateAddTable('rectangle')}>▬ {utils.useLang('RECTANGLE') || 'מלבן'}</button>
                    <button className="shape-btn triangle" onClick={() => initiateAddTable('triangle')}>▲ {utils.useLang('TRIANGLE') || 'משולש'}</button>
                </div>
            )}

            {/* Main Canvas Area */}
            <div className="canvas-wrapper">
                <CanvasTableMap
                    tables={displayedTables}
                    onTableUpdate={handleTableUpdate}
                    onTableSelect={(t, pos) => {
                        setSelectedTable(t);
                        if (pos) setPopupPosition(pos);
                    }}
                    role={mode === 'EDIT' ? role : 'host'}
                    selectedTableId={selectedTable?.id}
                />
            </div>

            {/* Table Popup Menu - positioned near table */}
            {mode === 'EDIT' && selectedTable && (
                <div
                    className="table-popup-menu"
                    style={{ left: popupPosition.x, top: popupPosition.y }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="popup-header">{selectedTable.name}</div>
                    <button className="popup-btn edit" onClick={() => initiateEditTable(selectedTable)}>
                        <Icon name="edit" size={16} style={{ verticalAlign: 'middle' }} /> {utils.useLang('EDIT') || "Edit"}
                    </button>
                    <button className="popup-btn lock" onClick={() => handleLockToggle(selectedTable)}>
                        <Icon name={selectedTable.is_locked ? "check" : "ban"} size={16} style={{ verticalAlign: 'middle' }} /> {selectedTable.is_locked ? (utils.useLang('UNLOCK') || "Unlock") : (utils.useLang('LOCK') || "Lock")}
                    </button>
                    <button className="popup-btn delete" onClick={() => handleDeleteTable(selectedTable.id)}>
                        <Icon name="trash" size={16} style={{ verticalAlign: 'middle' }} /> {utils.useLang('DELETE_TABLE') || "Delete"}
                    </button>
                </div>
            )}

            {/* New Table Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3 className="modal-header">
                            {editingTableId ? (utils.useLang('EDIT_TABLE') || 'עריכת שולחן') : (utils.useLang('ADD_TABLE_LABEL') || 'הוספת שולחן חדש')}
                        </h3>

                        <div className="form-group">
                            <label>{utils.useLang('TABLENAME') || 'שם השולחן'}</label>
                            <input
                                type="text"
                                value={newTableName}
                                onChange={e => setNewTableName(e.target.value)}
                                placeholder="לדוגמה: שולחן 12"
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label>{utils.useLang('DINERSNUMBER') || 'תפוסה'}</label>
                            <input
                                type="number"
                                value={newTableDiners}
                                onChange={e => setNewTableDiners(e.target.value)}
                                min="1"
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setShowModal(false)}>
                                {utils.useLang('CANCEL') || 'ביטול'}
                            </button>
                            <button className="btn-confirm" onClick={confirmAddOrUpdateTable}>
                                {editingTableId ? (utils.useLang('UPDATE') || 'עדכון') : (utils.useLang('APPROVE') || 'יצירה')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="modal-content delete-confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="modal-header"><Icon name="trash" size={18} style={{ verticalAlign: 'middle' }} /> {utils.useLang('CONFIRM_DELETE') || 'אישור מחיקה'}</h3>
                        <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '20px' }}>
                            {utils.useLang('WANT_DEL') || 'למחוק את השולחן?'}
                        </p>
                        <div className="modal-actions" style={{ justifyContent: 'center' }}>
                            <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                                {utils.useLang('CANCEL') || 'ביטול'}
                            </button>
                            <button className="btn-confirm" style={{ background: 'var(--danger)' }} onClick={confirmDeleteTable}>
                                {utils.useLang('DELETE') || 'מחיקה'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TablePage;
